import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CardMembership,
} from '@mui/icons-material';
import apiService from '../../services/api';

const SubscriptionPlanForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration_type: 'months',
    duration_value: 1,
    price: '',
    offer_price: '',
    bonus_number: '',
    bonus_type: 'months',
    currency: 'USD',
    billing_cycle: 'monthly',
    is_active: true,
    is_popular: false,
    sort_order: 0,
  });

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (isEditing) {
      fetchPlanDetails();
    }
  }, [id]);

  const fetchPlanDetails = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/superadmin/subscription-plan/${id}`);
       
      if (response.success) {
        const plan = response.data.plan;
        setFormData({
          name: plan.name || '',
          description: plan.description || '',
          duration_type: plan.duration_type || 'months',
          duration_value: plan.duration_value || 1,
          price: plan.price || '',
          offer_price: plan.offer_price || '',
          bonus_number: plan.bonus_number || '',
          bonus_type: plan.bonus_type || 'months',
          currency: plan.currency || 'USD',
          billing_cycle: plan.billing_cycle || 'monthly',
          is_active: (plan.is_active == 1) ? true : false,
          is_popular: (plan.is_popular == 1) ? true : false,
          sort_order: plan.sort_order || 0,
        });
      } else {
        setError('Failed to fetch plan details');
      }
    } catch (err) {
      setError('Error fetching plan details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Plan name is required';
    }
    
    if (formData.price === '' || parseFloat(formData.price) < 0) {
      errors.price = 'Price must be 0 or greater (0 for free trials)';
    }

    // Validate offer price if provided
    if (formData.offer_price !== '' && formData.offer_price !== null) {
      const offerPrice = parseFloat(formData.offer_price);
      const regularPrice = parseFloat(formData.price);
      
      if (offerPrice < 0) {
        errors.offer_price = 'Offer price must be 0 or greater';
      } else if (offerPrice > regularPrice) {
        errors.offer_price = 'Offer price cannot be higher than regular price';
      }
    }
    
    if (!formData.duration_value || formData.duration_value <= 0) {
      errors.duration_value = 'Valid duration value is required';
    }
    


    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload = {
        ...formData,
        is_active: formData.is_active ? 1 : 0,
        is_popular: formData.is_popular ? 1 : 0,
        price: parseFloat(formData.price),
        offer_price: formData.offer_price ? parseFloat(formData.offer_price) : null,
        bonus_number: formData.bonus_number ? parseInt(formData.bonus_number) : null,
        bonus_type: formData.bonus_type,
        duration_value: parseInt(formData.duration_value),
        sort_order: parseInt(formData.sort_order)
      };

      let response;
      if (isEditing) {
        response = await apiService.put(`/superadmin/subscription-plan/${id}`, payload);
      } else {
        response = await apiService.post('/superadmin/subscription-plan/new', payload);
      }

      if (response.success) {
        setSuccess(isEditing ? 'Plan updated successfully!' : 'Plan created successfully!');
        setTimeout(() => {
          navigate('/superadmin/subscription-plans');
        }, 1500);
      } else {
        setError(response.message || 'Failed to save plan');
      }
    } catch (err) {
      setError('Error saving plan: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/superadmin/subscription-plans');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading plan details...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', px: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleCancel}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <CardMembership sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            {isEditing ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchPlanDetails}
            disabled={!isEditing}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ marginTop: 5, width: '100%', maxWidth: '100%' }}>
        {/* Basic Information */}
        <Grid item xs={12} sx={{ width: '100%' }}>
          <Card sx={{ width: '100%', maxWidth: '100%' }}>
            <CardContent sx={{ width: '100%', p: 3 }}> 

              <Box sx={{ display: 'flex', gap: 2, marginBottom: 2  }}>
                <TextField
                  fullWidth
                  label="Plan Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={!!validationErrors.name}
                  helperText={validationErrors.name}
                  required 
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                  placeholder="Describe what this plan offers..." 
                />
              </Box>


              <Box sx={{ display: 'flex', gap: 2, marginBottom: 2  }}>
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  error={!!validationErrors.price}
                  helperText={validationErrors.price || "Enter 0 for free trials"}
                  placeholder="0.00"
                  required 
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Offer Price (Optional)"
                  type="number"
                  value={formData.offer_price}
                  onChange={(e) => handleInputChange('offer_price', e.target.value)}
                  error={!!validationErrors.offer_price}
                  helperText={validationErrors.offer_price || "Leave empty for no offer"}
                  placeholder="0.00"
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, marginBottom: 2  }}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={formData.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                    label="Currency"
                  >
                    <MenuItem value="USD">USD ($)</MenuItem>
                    <MenuItem value="EUR">EUR (€)</MenuItem>
                    <MenuItem value="GBP">GBP (£)</MenuItem>
                    <MenuItem value="INR">INR (₹)</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Billing Cycle</InputLabel>
                  <Select
                    value={formData.billing_cycle}
                    onChange={(e) => handleInputChange('billing_cycle', e.target.value)}
                    label="Billing Cycle"
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                    <MenuItem value="one_time">One Time</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, marginBottom: 2  }}>
                <TextField
                  fullWidth
                  label="Duration Value"
                  type="number"
                  value={formData.duration_value}
                  onChange={(e) => handleInputChange('duration_value', e.target.value)}
                  error={!!validationErrors.duration_value}
                  helperText={validationErrors.duration_value}
                  required 
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                />
                <FormControl fullWidth>
                  <InputLabel>Duration Type</InputLabel>
                  <Select
                    value={formData.duration_type}
                    onChange={(e) => handleInputChange('duration_type', e.target.value)}
                    label="Duration Type"
                  >
                    <MenuItem value="days">Days</MenuItem>
                    <MenuItem value="months">Months</MenuItem>
                    <MenuItem value="years">Years</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                   fullWidth
                   label="Sort Order"
                   type="number"
                   value={formData.sort_order}
                   onChange={(e) => handleInputChange('sort_order', e.target.value)}
                   helperText="Lower numbers appear first"
                   onWheel={(e) => e.target.blur()}
                   onKeyDown={(e) => {
                     if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                       e.preventDefault();
                     }
                   }}
                 />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, marginBottom: 2  }}>
                <TextField
                  fullWidth
                  label="Bonus Number (Optional)"
                  type="number"
                  value={formData.bonus_number}
                  onChange={(e) => handleInputChange('bonus_number', e.target.value)}
                  helperText="Extra time beyond main duration"
                  placeholder="0"
                  onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                />
                <FormControl fullWidth>
                  <InputLabel>Bonus Type</InputLabel>
                  <Select
                    value={formData.bonus_type}
                    onChange={(e) => handleInputChange('bonus_type', e.target.value)}
                    label="Bonus Type"
                  >
                    <MenuItem value="days">Days</MenuItem>
                    <MenuItem value="months">Months</MenuItem>
                  </Select>
                </FormControl>
              </Box>


 

              <Box sx={{ display: 'flex', gap: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    />
                  }
                  label="Active Plan"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_popular}
                      onChange={(e) => handleInputChange('is_popular', e.target.checked)}
                    />
                  }
                  label="Popular Plan"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                  onClick={handleSubmit}
                >
                  {saving ? 'Saving...' : (isEditing ? 'Update Plan' : 'Create Plan')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
  
      </Grid>
    </Box>
  );
};

export default SubscriptionPlanForm;
